import React, { useState, useRef, useEffect, useMemo } from 'react';
import { filterNodes, filterTreeByLeafPredicate } from '../utils/analyticsUtils';

const formatValue = (val) => {
  if (typeof val === 'boolean') return val ? 'True' : 'False';
  return val !== undefined && val !== null ? val : '';
};

const FilterDropdown = ({ col, data, currentFilters, onApply, onClose, anchorRect }) => {
  const dropdownRef = useRef(null);
  const [searchTerm, setSearchTerm] = useState('');

  const getUniqueValues = (nodes) => {
    let values = new Set();
    const traverse = (list) => {
      list.forEach(node => {
        const val = node.dimensions[col] !== undefined ? node.dimensions[col] : node.metrics[col];
        if (val !== undefined && val !== null) {
          values.add(val);
        }
        if (node.children) traverse(node.children);
      });
    };
    traverse(nodes);
    return Array.from(values).sort();
  };

  const allValues = useMemo(() => getUniqueValues(data), [data, col]);

  // Excel-style: with no active filter every value starts checked, so the user
  // can simply uncheck the ones to exclude. An active filter restores its set.
  const [tempSelected, setTempSelected] = useState(() => {
    const active = currentFilters[col];
    if (Array.isArray(active)) return active;
    if (active !== undefined) return [active];
    return allValues;
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const filteredValues = useMemo(() =>
    allValues.filter(v => String(v).toLowerCase().includes(searchTerm.toLowerCase())),
    [allValues, searchTerm]
  );

  const toggleSelect = (val) => {
    setTempSelected(prev =>
      prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
    );
  };

  // "All selected" means no filter at all, so applying clears it (and any new
  // value that appears later stays visible).
  const allSelected = allValues.length > 0 && allValues.every(v => tempSelected.includes(v));
  const handleSelectAll = () => setTempSelected(allValues);
  const handleClearAll = () => setTempSelected([]);
  const handleApply = () => onApply(allSelected ? [] : tempSelected);

  const isRightOverflow = anchorRect && (anchorRect.left + 1000 > window.innerWidth);
  const isBottomOverflow = anchorRect && (anchorRect.bottom + 400 > window.innerHeight);

  // Position is fixed relative to viewport, but anchored to the header cell
  const style = {
    position: 'fixed',
    top: anchorRect ? (isBottomOverflow ? 'auto' : `${anchorRect.bottom + 5}px`) : '100px',
    bottom: isBottomOverflow ? `${window.innerHeight - anchorRect.top + 5}px` : 'auto',
    left: anchorRect ? (isRightOverflow ? 'auto' : `${anchorRect.left}px`) : '100px',
    right: isRightOverflow ? `${window.innerWidth - anchorRect.right}px` : 'auto',
    backgroundColor: 'white',
    border: '1px solid #ccc',
    borderRadius: '4px',
    padding: '10px',
    zIndex: 9999,
    boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    minWidth: '220px',
    maxHeight: '400px',
    display: 'flex',
    flexDirection: 'column',
    color: '#333',
    fontWeight: 'normal'
  };

  return (
    <div style={style} ref={dropdownRef}>
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: '10px', padding: '6px', borderRadius: '4px', border: '1px solid #ddd' }}
      />
      <div style={{ marginBottom: '10px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span>
          <span onClick={handleSelectAll} style={{ color: '#007bff', cursor: 'pointer' }}>Select All</span>
          <span style={{ color: '#ccc', margin: '0 6px' }}>|</span>
          <span onClick={handleClearAll} style={{ color: '#007bff', cursor: 'pointer' }}>Clear</span>
        </span>
        <span style={{ backgroundColor: '#f0f0f0', borderRadius: '10px', padding: '2px 8px', fontSize: '11px', color: '#666' }}>
          <span style={{ color: '#007bff', fontWeight: 'bold' }}>{tempSelected.length}</span>
          {' / '}
          {allValues.length}
        </span>
      </div>
      <div style={{ flex: 1, overflowY: 'auto', marginBottom: '10px', textAlign: 'left', minHeight: '100px' }}>
        {filteredValues.length === 0 ? (
          <div style={{ padding: '10px', color: '#999', fontSize: '13px' }}>No values found</div>
        ) : (
          filteredValues.map(val => (
            <div 
              key={val} 
              onClick={() => toggleSelect(val)}
              style={{ 
                display: 'flex', 
                alignItems: 'center', 
                marginBottom: '5px',
                cursor: 'pointer',
                padding: '4px 6px',
                backgroundColor: tempSelected.includes(val) ? '#f0f7ff' : 'transparent',
                borderRadius: '2px',
                transition: 'background 0.2s'
              }}
            >
              <input
                type="checkbox"
                checked={tempSelected.includes(val)}
                readOnly
                style={{ marginRight: '8px' }}
              />
              <span style={{ fontSize: '13px' }}>{formatValue(val)}</span>
            </div>
          ))
        )}
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', borderTop: '1px solid #eee', paddingTop: '10px' }}>
        <button onClick={onClose} style={{ padding: '6px 12px', border: '1px solid #ccc', background: 'white', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
        <button
          onClick={handleApply}
          disabled={tempSelected.length === 0}
          style={{ padding: '6px 18px', background: tempSelected.length === 0 ? '#a5d6b0' : '#28a745', color: 'white', border: 'none', borderRadius: '4px', cursor: tempSelected.length === 0 ? 'not-allowed' : 'pointer', fontWeight: 'bold' }}
        >
          OK
        </button>
      </div>
    </div>
  );
};

const TableRow = React.memo(({ row, columns, dimensions, metrics, rowNumber, highlightMatch, highlightColor }) => {
  const [expanded, setExpanded] = useState(true);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const padLeft = `${row.level * 20}px`;

  const isLeaf = !row.children || row.children.length === 0;
  const isHighlighted = highlightColor && isLeaf && highlightMatch && highlightMatch(row);

  // First dimension gets the expand button if there are children
  return (
    <React.Fragment>
      <tr style={{ borderBottom: '1px solid #ddd', backgroundColor: isHighlighted ? `${highlightColor}33` : 'transparent' }}>
        <td style={{ 
          padding: '8px', 
          textAlign: 'center', 
          color: '#666', 
          fontSize: '12px',
          width: '50px',
          borderRight: '1px solid #eee'
        }}>
          {rowNumber}
        </td>
        {columns.map((col, index) => {
          let val = '';
          if (dimensions.includes(col)) {
            val = row.dimensions[col];
          } else if (metrics.includes(col)) {
            val = row.metrics[col];
          }

          if (index === 0) {
            return (
              <td key={col} style={{ padding: '8px', paddingLeft: padLeft }}>
                {row.children && row.children.length > 0 && (
                  <button
                    onClick={toggleExpand}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      marginRight: '8px',
                      fontSize: '12px'
                    }}
                  >
                    {expanded ? '▼' : '▶'}
                  </button>
                )}
                {formatValue(val)}
              </td>
            );
          }
          return <td key={col} style={{ padding: '8px' }}>{formatValue(val)}</td>;
        })}
      </tr>
      {expanded && row.children && row.children.length > 0 && (
        row.children.map((child, idx) => (
          <TableRow
            key={child.id}
            row={child}
            columns={columns}
            dimensions={dimensions}
            metrics={metrics}
            rowNumber={`${rowNumber}.${idx + 1}`}
            highlightMatch={highlightMatch}
            highlightColor={highlightColor}
          />
        ))
      )}
    </React.Fragment>
  );
});

const API_FILTER_KEYS = [
  "Class", "ExamType", "SegmentId", "CourseId", 
  "SegmentCourseId", "AchievementId", "AchievementVideoId", "PartName"
];

const AnalyticsTable = React.memo(({ 
  data, 
  rawData,
  columns, 
  dimensions, 
  metrics, 
  apiFilters, 
  setApiFilters,
  localFilters,
  setLocalFilters,
  columnValues,
  sortRules,
  setSortRules,
  highlightMatch,
  highlightColor
}) => {
  const [openFilter, setOpenFilter] = useState(null); // Key of the column whose filter is open
  const [anchorRect, setAnchorRect] = useState(null); // Rect for the filter anchor

  const requestSort = (key) => {
    setSortRules(prevRules => {
      const existingRuleIndex = prevRules.findIndex(rule => rule.key === key);
      
      if (existingRuleIndex === -1) {
        // Not in list: Add as new primary (or last) sort rule
        return [...prevRules, { key, direction: 'asc' }];
      }
      
      const existingRule = prevRules[existingRuleIndex];
      if (existingRule.direction === 'asc') {
        // Toggle to desc
        const newRules = [...prevRules];
        newRules[existingRuleIndex] = { ...existingRule, direction: 'desc' };
        return newRules;
      } else {
        // Remove from list
        return prevRules.filter(rule => rule.key !== key);
      }
    });
  };

  const handleApplyFilter = (key, value) => {
    // value is now an array
    const hasValues = Array.isArray(value) && value.length > 0;
    
    if (API_FILTER_KEYS.includes(key)) {
      setApiFilters(prev => {
        const newFilters = { ...prev };
        if (hasValues) {
          newFilters[key] = value;
        } else {
          delete newFilters[key];
        }
        return newFilters;
      });
    } else {
      setLocalFilters(prev => {
        const newFilters = { ...prev };
        if (hasValues) {
          newFilters[key] = value;
        } else {
          delete newFilters[key];
        }
        return newFilters;
      });
    }
    setOpenFilter(null);
    setAnchorRect(null);
  };

  const getDropdownData = (col) => {
    // Server-side filter columns: the returned data is already narrowed to the
    // selected value, so build options from the cached full value set (a tiny
    // synthetic tree). Otherwise the dropdown could only show the value you
    // already picked and you couldn't widen it again.
    if (API_FILTER_KEYS.includes(col)) {
      const values = columnValues && columnValues[col] ? columnValues[col] : [];
      return values.map((v) => ({ dimensions: { [col]: v }, children: [] }));
    }
    // Client-side columns: reflect the active criteria drill-in plus other active
    // filters, so options only show values that exist in what's currently shown.
    let base = rawData || [];
    if (highlightMatch) {
      base = filterTreeByLeafPredicate(base, highlightMatch);
    }
    const filtersWithoutCol = { ...localFilters };
    delete filtersWithoutCol[col];
    return filterNodes(base, filtersWithoutCol);
  };


  if (!data || data.length === 0) {
    return <div style={{ padding: '20px', textAlign: 'center' }}>No data available</div>;
  }

  return (
    <div style={{ overflowX: 'auto', maxHeight: '600px', border: '1px solid #ddd' }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#f4f4f4', zIndex: 10, boxShadow: '0 2px 2px -1px rgba(0,0,0,0.1)' }}>
          <tr>
            <th style={{ 
              padding: '12px 8px', 
              borderBottom: '2px solid #ddd', 
              textAlign: 'center',
              width: '50px',
              color: '#666'
            }}>#</th>
            {columns.map(col => {
              const isDimension = dimensions.includes(col);
              const ruleIndex = sortRules.findIndex(r => r.key === col);
              const currentRule = ruleIndex !== -1 ? sortRules[ruleIndex] : null;
              const hasFilter = !!apiFilters[col] || !!localFilters[col];

              return (
                <th 
                  key={col} 
                  style={{ 
                    padding: '12px 8px', 
                    borderBottom: '2px solid #ddd',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <span
                      onClick={() => requestSort(col)}
                      style={{ cursor: 'pointer', flex: 1 }}
                    >
                      {col === 'Name' ? 'SubCourseTopicName' : col}
                    </span>

                    <span
                      onClick={() => requestSort(col)}
                      style={{ marginLeft: '8px', fontSize: '10px', color: currentRule ? '#007bff' : '#999', cursor: 'pointer' }}
                    >
                      {currentRule
                        ? `${currentRule.direction === 'asc' ? '▲' : '▼'}${sortRules.length > 1 ? ` (${ruleIndex + 1})` : ''}`
                        : '↕'}
                    </span>

                    {isDimension && (
                      <span 
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = e.currentTarget.getBoundingClientRect();
                          setAnchorRect(rect);
                          setOpenFilter(openFilter === col ? null : col);
                        }}
                        style={{ 
                          marginLeft: '8px', 
                          cursor: 'pointer', 
                          color: hasFilter ? '#28a745' : '#999',
                          fontSize: '12px'
                        }}
                        title="Filter"
                      >
                        {hasFilter ? '⏳' : '☰'}
                      </span>
                    )}
                  </div>

                  {openFilter === col && (
                    <FilterDropdown
                      col={col}
                      // data={API_FILTER_KEYS.includes(col) ? (rawData || []) : (data || [])}
                      data={getDropdownData(col)}
                      currentFilters={{ ...apiFilters, ...localFilters }}
                      onApply={(values) => handleApplyFilter(col, values)}
                      onClose={() => {
                        setOpenFilter(null);
                        setAnchorRect(null);
                      }}
                      anchorRect={anchorRect}
                    />
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {data.map((row, index) => (
            <TableRow
              key={row.id}
              row={row}
              columns={columns}
              dimensions={dimensions}
              metrics={metrics}
              rowNumber={index + 1}
              highlightMatch={highlightMatch}
              highlightColor={highlightColor}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default AnalyticsTable;
